import React, { useState, useEffect } from 'react';
import { analytics } from '../utils/analytics';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Link,
  InputAdornment,
  IconButton,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { OAuthButtonGroup } from '../components/OAuthButton';
import useOAuth from '../hooks/useOAuth';
import api from '../services/api'; // Use centralized API service
import { PageShell } from '../components/layout';
import { ErrorState } from '../components/common';

const Login = () => {
  const theme = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // ProtectedRoute stores the page that sent the user here; go back there after login.
  const redirectTo = location.state?.from?.pathname
    ? `${location.state.from.pathname}${location.state.from.search || ''}`
    : '/dashboard';
  const { login } = useAuth();
  const { showNotification } = useNotification();
  const { availableProviders, initiateOAuth, loading: oauthLoading, error: oauthError } = useOAuth();

  const handleOAuthLogin = (provider) => {
    if (oauthLoading) return;
    analytics.oauthLoginClick(provider);
    try {
      initiateOAuth(provider, { returnTo: redirectTo });
    } catch (error) {
      showNotification(`Failed to initiate ${provider} login`, 'error');
    }
  };

  // Show OAuth error if any
  useEffect(() => {
    if (oauthError) {
      showNotification(oauthError, 'error');
    }
  }, [oauthError, showNotification]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrors({});
    
    const newErrors = {};
    if (!email) newErrors.email = 'Email is required';
    if (!password) newErrors.password = 'Password is required';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await api.post('/auth/signin', { email, password });
      const data = response.data || response; // Handle both new and legacy formats

      if (data.token) {
        // Debug: Log the user data from backend
        // console.log('🔍 Backend login response user data:', data.user);
        // console.log('🔍 User roles:', data.user?.roles);
        // console.log('🔍 User primary_role:', data.user?.primary_role);
        
        // Add permissions to user object before passing to login
        const userWithPermissions = {
          ...data.user,
          permissions: data.permissions || []
        };
        
        // Pass token and user in correct order as expected by AuthContext
        await login(userWithPermissions, data.token);  // Now includes permissions
        analytics.login('email');
        
        // Go straight to the page the user asked for, or the dashboard.
        navigate(redirectTo, { replace: true });
      }
    } catch (err) {
      const isInvalidCredentials = err?.statusCode === 401;
      const errorMessage = isInvalidCredentials
        ? 'Invalid email or password. Please try again.'
        : (err.message || 'Login failed');

      showNotification(errorMessage, 'error');

      if (errorMessage.toLowerCase().includes('email')) {
        setErrors(prev => ({ ...prev, email: errorMessage }));
      } else if (errorMessage.toLowerCase().includes('password')) {
        setErrors(prev => ({ ...prev, password: errorMessage }));
      } else {
        setErrors(prev => ({ ...prev, general: errorMessage }));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageShell hideChat kind="form" dots containerSx={{ py: { xs: 2, sm: 8 }, justifyContent: 'center' }}>
      <Box sx={{ mb: { xs: 2, sm: 4 }, textAlign: 'center' }}>
        <RouterLink to="/" style={{ textDecoration: 'none' }}>
          <Typography
            variant="h3"
            component="div"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              ...theme.modern.utils.gradientText('primary'),
              mb: 1,
              fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
            }}
          >
            {/* <HomeIcon sx={{ color: theme.modern.colors.modern.mainColor[500] }} /> */}
            Welcome Back
          </Typography>
        </RouterLink>
        
        <Typography 
          variant="body1" 
          sx={{ 
            ...theme.modern.typography.modernSubtext 
          }}
        >
          {location.state?.reason === 'cma'
            ? 'Log in to get your CMA report'
            : 'Log in to your account to continue'}
        </Typography>
      </Box>

      <Paper
        variant="modern"
        elevation={0}
        sx={{
          p: { xs: 2, sm: 4 },
          maxWidth: '100%',
          overflow: 'hidden',
        }}
      >
        <form onSubmit={handleLogin}>
          <Grid container spacing={3}>
            {/* OAuth Providers Section */}
            {availableProviders.length > 0 && (
              <>
                <Grid size={12}>
                  <OAuthButtonGroup
                    providers={availableProviders}
                    onProviderSelect={handleOAuthLogin}
                    disabled={isSubmitting || oauthLoading}
                    orientation="vertical"
                  />
                </Grid>

                <Grid size={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', my: 1 }}>
                    <Divider sx={{ flex: 1 }} />
                    <Typography variant="body2" sx={{ px: 2, color: '#64748b' }}>
                      OR
                    </Typography>
                    <Divider sx={{ flex: 1 }} />
                  </Box>
                </Grid>
              </>
            )}

            {/* Fallback: Show message if no OAuth providers are available */}
            {availableProviders.length === 0 && (
              <Grid size={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Social login is currently unavailable. Please use email and password to sign in.
                </Alert>
              </Grid>
            )}

            <Grid size={12}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0.5rem',
                  },
                }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={!!errors.password}
                helperText={errors.password}
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0.5rem',
                  },
                }}
              />
            </Grid>
            {errors.general && (
              <Grid size={12}>
                <ErrorState error={errors.general} />
              </Grid>
            )}
            <Grid size={12} sx={{ textAlign: 'right' }}>
              <Link
                component={RouterLink}
                to="/forgot-password"
                variant="body2"
                underline="hover"
                onClick={() => analytics.forgotPasswordClick()}
                sx={{ color: 'primary.main' }}
              >
                Forgot password?
              </Link>
            </Grid>
            <Grid size={12}>
              <Button
                type="submit"
                fullWidth
                variant="gradient"
                size="large"
                disabled={isSubmitting}
                sx={{
                  py: 1.5,
                }}
              >
                {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Log in'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Don't have an account?{' '}
          <Link
            component={RouterLink}
            to="/signup"
            state={location.state}
            variant="body2"
            underline="hover"
            sx={{ fontWeight: 600, color: 'primary.main' }}
          >
            Sign up
          </Link>
        </Typography>
      </Box>
    </PageShell>
  );
};

export default Login;
